import { ChevronDownIcon, CloseIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Flex,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuOptionGroup,
  Radio,
  RadioGroup,
  Switch,
  Text,
  Tooltip,
  useMediaQuery,
  useToast,
  VStack,
} from '@chakra-ui/react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- useEffect used in block fetch
import { Suspense, lazy, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearStepperFormData,
  createAction,
  getEditActionForm,
  getFormValues,
  getNoheadersStatus,
  handleActionStepPrevious,
  getPendingDataActionPayload,
  getSelectedDataFileDetails,
  getSyncOptions,
  putActionData,
  setActionCreateForm,
  setClearSelectedAreasTable,
  setPendingDataActionPayload,
  setSyncOptions,
} from 'redux/DataImportSlice';
import { getDimensionProperties } from 'redux/ItemPageSlice';
/* eslint-disable-next-line @typescript-eslint/no-unused-vars -- fetchBlock used in useEffect */
import { fetchBlock, getBlockDetails } from 'redux/PlanPageSlice';
import Spinner from 'components/Spinner';

const LazyMappingDimensionModal = lazy(() => import('../MappingDimensionModal'));
const LazyMappingDataModal = lazy(() => import('../MappingDataModel'));

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FilterCondition {
  property_id: string;
  operator: string;
  value: string;
}

type ActionType = 'dimension' | 'data' | 'forecast';

interface Properties {
  /** Which import type this step is being rendered for. */
  actionType: ActionType;
  setHelpModalSeleceted?: unknown;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DIMENSION_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'in', label: 'is one of' },
  { value: 'not_in', label: 'is not one of' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const VALUELESS_OPERATORS = new Set(['is_empty', 'is_not_empty']);

// ---------------------------------------------------------------------------
// FilterConditionRow (dimension type)
// ---------------------------------------------------------------------------

interface FilterConditionRowProperties {
  condition: FilterCondition;
  index: number;
  properties: any[];
  onUpdate: (index: number, update: Partial<FilterCondition>) => void;
  onRemove: (index: number) => void;
}

// Shared style for dropdown trigger buttons — matches the rest of the wizard
const dropdownButtonStyle = {
  backgroundColor: 'white',
  height: '32px',
  borderRadius: '5px',
  textAlign: 'left' as const,
  padding: '0 0.6rem',
  boxShadow: 'rgb(139, 137, 177) 0px 0px 0px 0.6px',
  fontWeight: '300',
  fontSize: 'xs',
  color: 'black',
  border: '0.6px solid #8B89B1',
  minWidth: '0',
};

const FilterConditionRow = ({
  condition,
  index,
  properties,
  onUpdate,
  onRemove,
}: FilterConditionRowProperties) => {
  const needsValue = !VALUELESS_OPERATORS.has(condition.operator);
  const isIn = condition.operator === 'in' || condition.operator === 'not_in';

  const selectedProperty = properties.find((p: any) => String(p.id) === condition.property_id);
  const selectedOp = DIMENSION_OPERATORS.find((op) => op.value === condition.operator);

  return (
    <HStack w='full' spacing={2} align='center' py={1}>
      {/* Property dropdown */}
      <Menu closeOnSelect isLazy>
        <MenuButton
          as={Button}
          {...dropdownButtonStyle}
          w='150px'
          rightIcon={<ChevronDownIcon fontSize='sm' />}
        >
          <Text fontSize='xs' noOfLines={1} color={selectedProperty ? 'black' : 'gray.400'}>
            {selectedProperty?.name ?? 'Property…'}
          </Text>
        </MenuButton>
        <MenuList zIndex='dropdown' maxH='200px' overflowY='auto' className='scroll'>
          <MenuOptionGroup value={condition.property_id}>
            {properties.map((p: any) => (
              <MenuItem
                key={p.id}
                fontSize='xs'
                onClick={() => onUpdate(index, { property_id: String(p.id) })}
                fontWeight={condition.property_id === String(p.id) ? 600 : 400}
              >
                {p.name}
              </MenuItem>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>

      {/* Operator dropdown */}
      <Menu closeOnSelect isLazy>
        <MenuButton
          as={Button}
          {...dropdownButtonStyle}
          w='130px'
          rightIcon={<ChevronDownIcon fontSize='sm' />}
        >
          <Text fontSize='xs' noOfLines={1} color={selectedOp ? 'black' : 'gray.400'}>
            {selectedOp?.label ?? 'Operator…'}
          </Text>
        </MenuButton>
        <MenuList zIndex='dropdown'>
          <MenuOptionGroup value={condition.operator}>
            {DIMENSION_OPERATORS.map((op) => (
              <MenuItem
                key={op.value}
                fontSize='xs'
                onClick={() => onUpdate(index, { operator: op.value, value: '' })}
                fontWeight={condition.operator === op.value ? 600 : 400}
              >
                {op.label}
              </MenuItem>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>

      {/* Value input */}
      {needsValue && (
        <Tooltip label={isIn ? 'Separate multiple values with a comma' : ''} isDisabled={!isIn}>
          <Box>
            <Input
              value={condition.value}
              onChange={(event_) => onUpdate(index, { value: event_.target.value })}
              placeholder={isIn ? 'value1, value2' : 'Value'}
              size='sm'
              w='130px'
              h='32px'
              fontSize='xs'
              borderColor='#8B89B1'
              borderRadius='5px'
              color='black'
              _placeholder={{ color: 'gray.400' }}
              _focus={{ borderColor: '#6562CF', boxShadow: 'none' }}
            />
          </Box>
        </Tooltip>
      )}

      <IconButton
        aria-label='Remove condition'
        icon={<DeleteIcon />}
        size='xs'
        variant='ghost'
        colorScheme='red'
        onClick={() => onRemove(index)}
      />
    </HStack>
  );
};

// ---------------------------------------------------------------------------
// DimensionSyncOptions — remove missing items config
// ---------------------------------------------------------------------------

interface DimensionSyncOptionsProperties {
  properties: any[];
  syncOptions: any;
  onSyncOptionsChange: (options: any) => void;
}

const DimensionSyncOptions = ({
  properties,
  syncOptions,
  onSyncOptionsChange,
}: DimensionSyncOptionsProperties) => {
  const rm = syncOptions?.remove_missing ?? { enabled: false };
  const enabled: boolean = rm.enabled ?? false;
  const scopeMode: string = rm.scope_mode ?? 'all';
  const [conditions, setConditions] = useState<FilterCondition[]>(rm.filter?.conditions ?? []);

  const update = (patch: Partial<typeof rm>) => {
    const newRm = { ...rm, ...patch };
    onSyncOptionsChange({ remove_missing: newRm });
  };

  const handleToggle = (checked: boolean) => {
    update({ enabled: checked, scope_mode: checked ? scopeMode : 'all' });
    if (!checked) setConditions([]);
  };

  const handleScopeChange = (value: string) => {
    update({ scope_mode: value });
    if (value === 'all') setConditions([]);
  };

  const pushConditions = (next: FilterCondition[]) => {
    setConditions(next);
    const filter = next.length > 0 ? { logic: 'AND' as const, conditions: next } : undefined;
    update({ filter });
  };

  return (
    <VStack w='full' align='start' spacing={4}>
      <HStack spacing={3} align='center'>
        <Switch
          id='rm-toggle'
          colorScheme='teal'
          isChecked={enabled}
          onChange={(event_) => handleToggle(event_.target.checked)}
        />
        <FormLabel
          htmlFor='rm-toggle'
          mb={0}
          fontSize='sm'
          fontWeight={600}
          fontFamily='regular_roboto'
          color='black'
          cursor='pointer'
        >
          Remove missing items
        </FormLabel>
      </HStack>
      <Text fontSize='xs' color='gray.600' fontFamily='regular_roboto' ps='52px'>
        Remove items not present in this file after the import completes.
      </Text>

      <Collapse in={enabled} animateOpacity style={{ width: '100%' }}>
        <VStack w='full' align='start' spacing={3} ps={2}>
          <Text fontSize='sm' fontWeight={600} fontFamily='regular_roboto' color='black'>
            Removal scope
          </Text>
          <RadioGroup value={scopeMode} onChange={handleScopeChange}>
            <VStack align='start' spacing={2}>
              <Radio value='all' colorScheme='teal' size='md'>
                <Text fontSize='sm' fontFamily='regular_roboto' color='black'>
                  All items in this dimension
                </Text>
              </Radio>
              <Radio value='filter' colorScheme='teal' size='md'>
                <Text fontSize='sm' fontFamily='regular_roboto' color='black'>
                  Only remove items matching filters
                </Text>
              </Radio>
            </VStack>
          </RadioGroup>

          {scopeMode === 'filter' && (
            <VStack
              w='full'
              align='start'
              pt={2}
              ps={3}
              borderLeft='2px solid'
              borderColor='teal.300'
              spacing={2}
            >
              <Text fontSize='xs' fontWeight={600} color='black' fontFamily='regular_roboto'>
                Filter conditions (AND logic)
              </Text>
              {conditions.length === 0 && (
                <Text fontSize='xs' color='gray.500' fontFamily='regular_roboto'>
                  No conditions — all items are eligible. Add a condition to restrict.
                </Text>
              )}
              {conditions.map((c, conditionIndex) => (
                <FilterConditionRow
                  key={conditionIndex}
                  condition={c}
                  index={conditionIndex}
                  properties={properties}
                  onUpdate={(targetIndex, up) =>
                    pushConditions(
                      conditions.map((x, index_) => (index_ === targetIndex ? { ...x, ...up } : x)),
                    )
                  }
                  onRemove={(targetIndex) =>
                    pushConditions(conditions.filter((_, index_) => index_ !== targetIndex))
                  }
                />
              ))}
              <Button
                size='xs'
                variant='ghost'
                colorScheme='teal'
                leftIcon={<AddIcon />}
                fontFamily='regular_roboto'
                onClick={() =>
                  pushConditions([
                    ...conditions,
                    {
                      property_id: properties?.[0]?.id ? String(properties[0].id) : '',
                      operator: 'equals',
                      value: '',
                    },
                  ])
                }
              >
                Add condition
              </Button>
            </VStack>
          )}
        </VStack>
      </Collapse>
    </VStack>
  );
};

// ---------------------------------------------------------------------------
// DataForecastSyncOptions — clear before import config
// ---------------------------------------------------------------------------

interface DimScope {
  dimension_id: number;
  name: string;
  is_time: boolean;
  mode: 'matching' | 'all';
}

interface DataForecastSyncOptionsProperties {
  blockDimensions: DimScope[];
  syncOptions: any;
  onSyncOptionsChange: (options: any) => void;
}

const DataForecastSyncOptions = ({
  blockDimensions,
  syncOptions,
  onSyncOptionsChange,
}: DataForecastSyncOptionsProperties) => {
  const callback = syncOptions?.clear_before_import ?? { enabled: false };
  const enabled: boolean = callback.enabled ?? false;

  const defaultScopes = (): DimScope[] =>
    blockDimensions.map((d) => ({ ...d, mode: d.is_time ? 'matching' : 'all' }));

  const savedScopes: DimScope[] = (() => {
    if (!callback.dimension_scopes?.length) return defaultScopes();
    return blockDimensions.map((bd) => {
      const saved = callback.dimension_scopes.find((s: any) => s.dimension_id === bd.dimension_id);
      return { ...bd, mode: saved?.mode ?? (bd.is_time ? 'matching' : 'all') };
    });
  })();

  const [dimScopes, setDimScopes] = useState<DimScope[]>(savedScopes);

  const update = (patch: any) => {
    onSyncOptionsChange({ clear_before_import: { ...callback, ...patch } });
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      const scopes = dimScopes.map((d) => ({ dimension_id: d.dimension_id, mode: d.mode }));
      update({ enabled: true, dimension_scopes: scopes });
    } else {
      update({ enabled: false, dimension_scopes: [] });
    }
  };

  const handleScopeChange = (dimId: number, mode: 'matching' | 'all') => {
    const next = dimScopes.map((d) => (d.dimension_id === dimId ? { ...d, mode } : d));
    setDimScopes(next);
    update({
      dimension_scopes: next.map((d) => ({ dimension_id: d.dimension_id, mode: d.mode })),
    });
  };

  return (
    <VStack w='full' align='start' spacing={4}>
      <HStack spacing={3} align='center'>
        <Switch
          id='clear-toggle'
          colorScheme='teal'
          isChecked={enabled}
          onChange={(event_) => handleToggle(event_.target.checked)}
        />
        <FormLabel
          htmlFor='clear-toggle'
          mb={0}
          fontSize='sm'
          fontWeight={600}
          color='black'
          fontFamily='regular_roboto'
          cursor='pointer'
        >
          Clear before import
        </FormLabel>
      </HStack>
      <Text fontSize='xs' color='gray.600' fontFamily='regular_roboto' ps='52px'>
        Remove existing values before loading so this import fully reconciles with the source file.
      </Text>

      <Collapse in={enabled} animateOpacity style={{ width: '100%' }}>
        {blockDimensions.length > 0 ? (
          <VStack w='full' align='start' spacing={2} ps={2}>
            <Text fontSize='sm' fontWeight={600} fontFamily='regular_roboto' color='black'>
              Configure clearing scope for each dimension
            </Text>
            <Text fontSize='xs' color='gray.500' fontFamily='regular_roboto'>
              Matching — clears only values present in the file for this dimension. All — clears all
              existing values for this dimension.
            </Text>
            {dimScopes.map((dim) => (
              <Flex
                key={dim.dimension_id}
                w='full'
                align='center'
                justify='space-between'
                borderBottom='1px solid'
                borderColor='gray.100'
                py={2}
              >
                <Text fontSize='sm' fontFamily='regular_roboto' color='black' minW='120px'>
                  {dim.name}
                </Text>
                <RadioGroup
                  value={dim.mode}
                  onChange={(v) => handleScopeChange(dim.dimension_id, v as 'matching' | 'all')}
                >
                  <HStack spacing={4}>
                    <Radio value='matching' colorScheme='teal' size='sm'>
                      <Text fontSize='xs' color='black'>
                        {dim.is_time ? 'Matching periods' : 'Matching items'}
                      </Text>
                    </Radio>
                    <Radio value='all' colorScheme='teal' size='sm'>
                      <Text fontSize='xs' color='black'>
                        {dim.is_time ? 'All periods' : 'All items'}
                      </Text>
                    </Radio>
                  </HStack>
                </RadioGroup>
              </Flex>
            ))}
          </VStack>
        ) : (
          <Text fontSize='xs' color='gray.400' fontFamily='regular_roboto' ps={2}>
            Dimension information not available. Ensure a block is selected in Step 2.
          </Text>
        )}
      </Collapse>
    </VStack>
  );
};

// ---------------------------------------------------------------------------
// ActionStepSyncOptions — main export
// ---------------------------------------------------------------------------

/**
 * Final wizard step for all import action types.
 *
 * Renders "Remove missing items" config for dimensions and
 * "Clear before import" with per-dimension scope for data/forecast.
 *
 * On submit: creates/updates the action with all accumulated settings
 * (including the property mapping stored in Redux from step 3) and opens the
 * mapping preview modal.
 */
const ActionStepSyncOptions = ({ actionType }: Properties) => {
  const dispatch: any = useDispatch();
  const [isLaptop] = useMediaQuery('(max-width: 1200px)');
  const getFormValue = useSelector(getFormValues);
  const sourceFileId = useSelector(getSelectedDataFileDetails);
  const stepperFormEditable = useSelector(getEditActionForm);
  const isHeader = useSelector(getNoheadersStatus);
  const properties = useSelector(getDimensionProperties);
  const syncOptions = useSelector(getSyncOptions);
  const blockDetails = useSelector(getBlockDetails);
  const pendingDataActionPayload = useSelector(getPendingDataActionPayload);

  const [mappingModal, setMappingModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState<number | null>(null);
  const [selectedMapType, setSelectedMapType] = useState('');
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const toast = useToast();

  // Ensure block details are loaded when on Sync Options (data/forecast flow)
  const blockId = Number(getFormValue?.blockId);
  useEffect(() => {
    if (actionType !== 'dimension' && blockId && (!blockDetails || blockDetails.id !== blockId)) {
      dispatch(fetchBlock(blockId));
    }
  }, [actionType, blockId, blockDetails?.id, dispatch]);

  // Build dimension scope list from selected block (for data/forecast).
  // Use getBlockDetails (populated by fetchBlock in DataStepsForm) — has full dimensions.
  const blockDimensions: DimScope[] = (() => {
    if (actionType === 'dimension') return [];
    if (!blockId) return [];
    const block = blockDetails;
    if (!block || block.id !== blockId || !block?.dimensions) return [];
    return (block.dimensions as any[]).map((d: any) => ({
      dimension_id: d.id,
      name: d.name,
      is_time: d.name?.toLowerCase() === 'time',
      mode: d.name?.toLowerCase() === 'time' ? 'matching' : 'all',
    }));
  })();

  const handleSyncOptionsChange = (options: any) => {
    dispatch(setSyncOptions(options));
  };

  const handleSubmit = async () => {
    setSaveError(null);
    setIsSubmitting(true);
    try {
      /**
       * Dev-only: fake a failed save (same UX as HTTP 500) without hitting the API.
       * In the browser console: localStorage.setItem('DEBUG_FORCE_ACTION_SAVE_ERROR', '1')
       * To turn off: localStorage.removeItem('DEBUG_FORCE_ACTION_SAVE_ERROR')
       */
      if (
        process.env.NODE_ENV === 'development' &&
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('DEBUG_FORCE_ACTION_SAVE_ERROR') === '1'
      ) {
        throw new Error(
          'Simulated server error (HTTP 500). Remove localStorage key DEBUG_FORCE_ACTION_SAVE_ERROR to disable.',
        );
      }

      if (actionType === 'dimension') {
        // Dimension: build request from form values
        const dimId = getFormValue?.dimension;
        const requestBody: any = {
          name: getFormValue?.name,
          description: getFormValue?.description,
          action_type: 'dimension',
          mapping_type: 'row',
          time_column: 'time',
          period_type: 'single',
          missing_header: isHeader,
          sync_options: syncOptions ?? null,
          dimension_id: dimId,
          // Carried over from the "Select scenario" field on step 2
          // (DimensionType.tsx) so the scenario chosen there is the one this
          // action actually runs against, rather than whatever the backend
          // would default to.
          scenario_id: getFormValue?.selectedScenario || undefined,
          mappings: {
            [dimId]: getFormValue?.column,
            property_map: getFormValue?.propertdata ?? {},
          },
        };
        if (stepperFormEditable?.isEditForm) {
          await dispatch(
            putActionData({
              dataSourceId: sourceFileId?.id,
              actionId: stepperFormEditable?.actionId,
              request_body: requestBody,
            }),
          ).unwrap();
          setSelectedActionId(stepperFormEditable?.actionId);
          setSelectedMapType(stepperFormEditable?.actionType);
          // Prefer what the user actually picked on step 2 — falls back to the
          // action's prior scenario if they never touched that field.
          setScenarioId(getFormValue?.selectedScenario || stepperFormEditable?.scenarioId);
        } else {
          const created = await dispatch(
            createAction({ dataSourceId: sourceFileId?.id, request_body: requestBody }),
          ).unwrap();
          setSelectedActionId(created?.id);
          setSelectedMapType(created?.action_type);
          setScenarioId(getFormValue?.selectedScenario || created?.scenario_id);
        }
        dispatch(setClearSelectedAreasTable());
        setMappingModal(true);
      } else {
        // Data/forecast: use pending payload from DimensionSelectionSteps, merge sync_options
        const pending = pendingDataActionPayload;
        if (!pending?.request_body) {
          const message = 'Import configuration is missing. Go back a step and try again.';
          setSaveError(message);
          toast({
            title: 'Cannot continue',
            description: message,
            status: 'warning',
            duration: 8000,
            isClosable: true,
          });
          return;
        }
        const requestBody = {
          ...pending.request_body,
          sync_options: syncOptions ?? null,
        };
        if (pending.isEditForm && pending.actionId) {
          await dispatch(
            putActionData({
              dataSourceId: sourceFileId?.id,
              actionId: pending.actionId,
              request_body: requestBody,
            }),
          ).unwrap();
          setSelectedActionId(pending.actionId);
          setSelectedMapType(stepperFormEditable?.actionType ?? 'data');
          setScenarioId(getFormValue?.selectedScenario);
        } else {
          const created = await dispatch(
            createAction({ dataSourceId: sourceFileId?.id, request_body: requestBody }),
          ).unwrap();
          setSelectedActionId(created?.id);
          setSelectedMapType(created?.action_type);
          setScenarioId(created?.scenario_id);
        }
        dispatch(setPendingDataActionPayload(null));
        dispatch(setClearSelectedAreasTable());
        setMappingModal(true);
      }
    } catch (error: unknown) {
      const message =
        typeof error === 'string'
          ? error
          : error instanceof Error && error.message
          ? error.message
          : 'Something went wrong while saving the action. Please try again, or contact support if it keeps happening.';
      setSaveError(message);
      toast({
        title: 'Could not save the action',
        description: message,
        status: 'error',
        duration: 12_000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack display='flex' justify='center' w='100%' pt={3} align='stretch'>
      {/* Header: title left, close button top-right — matches DimensionSelectionSteps */}
      <Flex justifyContent='space-between' w='full' align='center'>
        <Text
          color='#6562CF'
          fontSize='lg'
          fontFamily='regular_roboto'
          fontWeight='400'
          textAlign='start'
        >
          Sync options
        </Text>
        <CloseIcon
          color='black'
          width={3}
          height={3}
          cursor='pointer'
          onClick={async () => {
            await dispatch(setActionCreateForm(false));
            await dispatch(clearStepperFormData());
            await dispatch(setClearSelectedAreasTable());
          }}
        />
      </Flex>

      <VStack w='full' pt={4} pb={2} align='stretch' spacing={5}>
        {actionType === 'dimension' ? (
          <DimensionSyncOptions
            properties={properties ?? []}
            syncOptions={syncOptions}
            onSyncOptionsChange={handleSyncOptionsChange}
          />
        ) : (
          <DataForecastSyncOptions
            blockDimensions={blockDimensions}
            syncOptions={syncOptions}
            onSyncOptionsChange={handleSyncOptionsChange}
          />
        )}
      </VStack>

      {saveError && (
        <Alert status='error' borderRadius='md' fontSize='sm' alignItems='flex-start'>
          <AlertIcon mt={0.5} />
          <Box>
            <AlertTitle fontSize='sm'>Could not save the action</AlertTitle>
            <AlertDescription fontSize='xs' mt={1}>
              {saveError} You can adjust sync options or go back to check mappings, then try
              Continue again.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Buttons bottom-right — matches DimensionSelectionSteps exactly */}
      <Flex alignItems='center' justifyContent='flex-end' pb={8} pt='5' w='100%'>
        <Flex my={2} color='black.900' fontSize='xs' display='flex' alignContent='center'>
          <Button
            width='80px'
            height='30px'
            borderRadius='xs'
            fontSize='xs'
            bgColor='#EFEEFE'
            color='#6562CF'
            fontWeight='400'
            me='1rem'
            fontFamily='Roboto Regular'
            px={isLaptop ? '8' : '10'}
            py={isLaptop ? '5' : '6'}
            onClick={() => dispatch(handleActionStepPrevious())}
            textTransform='capitalize'
          >
            Previous
          </Button>
          <Button
            _hover={{ _disabled: {} }}
            borderRadius='xs'
            fontWeight='400'
            fontFamily='Roboto Regular'
            width='fit-content'
            px={isLaptop ? '6' : '10'}
            py={isLaptop ? '5' : '6'}
            height='30px'
            fontSize='xs'
            bgColor='#4BB6B9'
            textTransform='capitalize'
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText='Saving…'
            color='white'
          >
            Continue
          </Button>
        </Flex>
      </Flex>

      <Suspense fallback={<Spinner />}>
        {actionType === 'dimension' ? (
          <LazyMappingDimensionModal
            mappingModal={mappingModal}
            setMappingModal={{ on: () => setMappingModal(true), off: () => setMappingModal(false) }}
            selectedActionId={selectedActionId}
            dataSourceId={sourceFileId?.id}
            selectedMapType={selectedMapType}
            scenarioId={scenarioId}
          />
        ) : (
          <LazyMappingDataModal
            mappingModal={mappingModal}
            setMappingModal={{ on: () => setMappingModal(true), off: () => setMappingModal(false) }}
            selectedActionId={selectedActionId}
            dataSourceId={sourceFileId?.id}
            selectedMapType={selectedMapType}
            scenarioId={scenarioId}
          />
        )}
      </Suspense>
    </VStack>
  );
};

export default ActionStepSyncOptions;
